/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Default C2PA timestamp-trust anchors: the self-signed root certificates of the
 * official C2PA trust list (https://contentcredentials.org/trust/anchors.pem).
 * Used to evaluate RFC 3161 TSA-chain trust when no trust anchors are supplied
 * (trust verification off), mirroring c2pa-rs's built-in timestamp trust: an
 * untrusted-TSA timestamp is not honored for certificate-validity, so an expired
 * signing certificate is judged against `now`. Trust-on uses the caller-supplied
 * anchors instead (which may add intermediate TSA CAs).
 *
 * Roots (19):
 *  - Leica Camera AG — Leica C2PA Root CA
 *  - Microsoft Corporation — Microsoft Supply Chain RSA Root CA 2022
 *  - Adobe Systems Incorporated — Adobe Root CA G2
 *  - Truepic — RootCA
 *  - Microsoft Corporation — Microsoft Identity Verification Root Certificate Authority 2020
 *  - ContentSign by Nodle — ContentSign Root CA
 *  - Samsung corporation
 *  - Metaphysic PRO — MetaphysicRootCA
 *  - Canon Inc. — Canon C2PA Root CA
 *  - FUJIFILM Corporation — FUJIFILM C2PA Root CA G1
 *  - Pinterest Inc. — Pinterest Root Certificate Authority
 *  - ATOM Technologies — ATOM root CA v1
 *  - Trufo (Root) — trufo.ai
 *  - vivo Mobile Communication Co., Ltd. — vivo Content Provenance and Authenticity Root CA
 *  - NIKON CORPORATION — Nikon C2PA Root CA
 *  - SONY Corporation — SONY C2PA Root CA G2
 *  - Cybertrust Japan Co., Ltd. — Cybertrust iTrust C2PA Root Certification Authority G1
 *  - Bria Artificial Intelligence — Bria Artificial Intelligence C2PA Root CA
 *  - Microsoft Corporation — Microsoft C2PA AL2 Root CA 2025
 *
 * The matching EKU policy (id-kp-timeStamping etc.) is in DEFAULT_TRUST_CONFIG.
 * Regenerate via: npm run gen:roots  (scripts/gen-roots.ts).
 */

export const DEFAULT_C2PA_TRUST_ANCHORS = `
-----BEGIN CERTIFICATE-----
MIIDCDCCAq2gAwIBAgIQfj2771gNZMLyE3lSWlq8UDAKBggqhkjOPQQDAjCBojEL
MAkGA1UEBhMCREUxGDAWBgNVBAoTD0xlaWNhIENhbWVyYSBBRzEbMBkGA1UEAxMS
TGVpY2EgQzJQQSBSb290IENBMRAwDgYDVQQHEwdXZXR6bGFyMQ4wDAYDVQQREwUz
NTU3ODEYMBYGA1UECRMPQW0gTGVpdHotUGFyayA1MQ8wDQYDVQQFEwYyMDIzLTEx
DzANBgNVBAgTBkhlc3NlbjAgFw0yMzA3MDQxMjM1MzNaGA8yMDczMDcwNDEyMzUz
M1owgaIxCzAJBgNVBAYTAkRFMRgwFgYDVQQKEw9MZWljYSBDYW1lcmEgQUcxGzAZ
BgNVBAMTEkxlaWNhIEMyUEEgUm9vdCBDQTEQMA4GA1UEBxMHV2V0emxhcjEOMAwG
A1UEERMFMzU1NzgxGDAWBgNVBAkTD0FtIExlaXR6LVBhcmsgNTEPMA0GA1UEBRMG
MjAyMy0xMQ8wDQYDVQQIEwZIZXNzZW4wWTATBgcqhkjOPQIBBggqhkjOPQMBBwNC
AATYWHZLNiNnug3OVuNy0DbdTFEDDuVfzZeqis2yX2AgqZ9fM2R7UqC01v5pMx/N
xRMSV/Q/DD6wR0dwtkXaxohko4HAMIG9MA8GA1UdEwEB/wQFMAMBAf8wHQYDVR0O
BBYEFK1aJ39jgaFAW/vnqbxTtDBFVI77MDUGCCsGAQUFBwEBBCkwJzAlBggrBgEF
BQcwAYYZaHR0cDovL29jc3AubGVpY2Euc3lzdGVtczAOBgNVHQ8BAf8EBAMCAQYw
RAYDVR0fBD0wOzA5oDegNYYzaHR0cDovL2NybC5sZWljYS5zeXN0ZW1zL2NybC9s
ZWljYV9jMnBhX3Jvb3RfY2EuY3JsMAoGCCqGSM49BAMCA0kAMEYCIQCMmLG+9WqL
EFo+xkgDMaihJTTpbWDfSCcNrMfb9KEl+wIhAORyQm7Wchx4fmMQKYubFjeYCZtP
u+FSiisFK83vwhTQ
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIFrzCCA5egAwIBAgIQaCjVTH5c2r1DOa4MwVoqNTANBgkqhkiG9w0BAQwFADBf
MQswCQYDVQQGEwJVUzEeMBwGA1UEChMVTWljcm9zb2Z0IENvcnBvcmF0aW9uMTAw
LgYDVQQDEydNaWNyb3NvZnQgU3VwcGx5IENoYWluIFJTQSBSb290IENBIDIwMjIw
HhcNMjIwMjE3MDAxMjM2WhcNNDcwMjE3MDAyMTA5WjBfMQswCQYDVQQGEwJVUzEe
MBwGA1UEChMVTWljcm9zb2Z0IENvcnBvcmF0aW9uMTAwLgYDVQQDEydNaWNyb3Nv
ZnQgU3VwcGx5IENoYWluIFJTQSBSb290IENBIDIwMjIwggIiMA0GCSqGSIb3DQEB
AQUAA4ICDwAwggIKAoICAQCeJQFmGR9kNMGdOSNiHXGLVuol0psf7ycBgr932JQz
gxhIm1Cee5ZkwtDDX0X/MpzoFxe9eO11mF86BggrHDebRkqQCrCvRpI+M4kq+rjn
MmPzI8du0hT7Jlju/gaEVPrBHzeq29TsViq/Sb3M6wLtxk78rBm1EjVpFYkXTaNo
6mweKZoJ8856IcYJ0RnqjzBGaTtoBCt8ii3WY13qbdY5nr0GPlvuLxFbKGunUqRo
Xkyk6q7OI79MNnHagUVQjsqGzv9Tw7hDsyTuB3qitPrHCh17xlI1MewIH4SAklv4
sdo51snn5YkEflF/9OZqZEdJ6vjspvagQ1P+2sMjJNgl2hMsKrc/lN53HEx4HGr5
mo/rahV3d61JhM4QQMeZSA/Vlh6AnHOhOKEDb9NNINC1Q+T3LngPTve8v2XabZAL
W7/e6icnmWT4OXxzPdYh0u7W81MRLlXD3OrxKVfeUaF4c5ALL/XJdTbrjdJtjnld
uho4/98ZAajSyNHW8uuK9S7RzJMTm5yQeGVjeQTE8Z6fjDrzZAz+mB2T4o9WpWNT
I7hucxZFGrb3ew/NpDL/Wv6WjeGHeNtwg6gkhWkgwm0SDeV59ipZz9ar54HmoLGI
LQiMC7HP12w2r575A2fZQXOpq0W4cWBYGNQWLGW60QXeksVQEBGQzkfM+6+/I8Cf
BQIDAQABo2cwZTAOBgNVHQ8BAf8EBAMCAYYwDwYDVR0TAQH/BAUwAwEB/zAdBgNV
HQ4EFgQUC7NoO6/ar+5wpXbZIffMRBYH0PgwEAYJKwYBBAGCNxUBBAMCAQAwEQYD
VR0gBAowCDAGBgRVHSAAMA0GCSqGSIb3DQEBDAUAA4ICAQBIxzf//8FoV9eLQ2ZG
OiZrL+j63mihj0fxPTSVetpVMfSV0jhfLLqPpY1RMWqJVWhsK0JkaoUkoFEDx93R
cljtbB6M2JHF50kRnRl6N1ged0T7wgiYQsRN45uKDs9ARU8bgHBZjJOB6A/VyCaV
qfcfdwa4yu+c++hm2uU54NLSYsOn1LYYmiebJlBKcpfVs1sqpP1fL37mYqMnZgz6
2RnMER0xqAFSCOZUDJljK+rYhNS0CBbvvkpbiFj0Bhag63pd4cdE1rsvVVYl8J4M
5A8S28B/r1ZdxokOcalWEuS5nKhkHrVHlZKu0HDIk318WljxBfFKuGxyGKmuH1eZ
JnRm9R0P313w5zdbX7rwtO/kYwd+HzIYaalwWpL5eZxY1H6/cl1TRituo5lg1oWM
ZncWdq/ixRhb4l0INtZmNxdl8C7PoeW85o0NZbRWU12fyK9OblHPiL6S6jD7LOd1
P0JgxHHnl59zx5/K0bhsI+pQKB0OQ8z1qRtA66aY5eUPxZIvpZbH1/o8GO4dG2ED
/YbnJEEzvdjztmB88xyCA9Vgr9/0IKTkgQYiWsyFM31k+OS4v4AX1PshP2Ou54+3
F0Tsci41yQvQgR3pcgMJQdnfCUjmzbeyHGAlGVLzPRJJ7Z2UIo5xKPjBB1Rz3TgI
tIWPFGyqAK9Aq7WHzrY5XHP5kA==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIFpDCCA4ygAwIBAgIQXfEvX1enw+GwAtiTJwzd4TANBgkqhkiG9w0BAQsFADBs
MQswCQYDVQQGEwJVUzEjMCEGA1UEChMaQWRvYmUgU3lzdGVtcyBJbmNvcnBvcmF0
ZWQxHTAbBgNVBAsTFEFkb2JlIFRydXN0IFNlcnZpY2VzMRkwFwYDVQQDExBBZG9i
ZSBSb290IENBIEcyMB4XDTE2MTEyOTAwMDAwMFoXDTQ2MTEyODIzNTk1OVowbDEL
MAkGA1UEBhMCVVMxIzAhBgNVBAoTGkFkb2JlIFN5c3RlbXMgSW5jb3Jwb3JhdGVk
MR0wGwYDVQQLExRBZG9iZSBUcnVzdCBTZXJ2aWNlczEZMBcGA1UEAxMQQWRvYmUg
Um9vdCBDQSBHMjCCAiIwDQYJKoZIhvcNAQEBBQADggIPADCCAgoCggIBALbacmKb
7oN7MqJG44ojMpdXrC1zdFHLBv97MuaXDKzI59kgZ0hK900lgU8iXW9p2iwYQs/F
bJ0+cTRxUlKhthpbnRTNu42R5LGI+XAPbQznxqfr82ScT11BwF/mF4hATOsDy5Xv
sqXmjji9HCN5V8MicQTJcQ6zK9W9U52m7lLt3vK1T/eQKFL9UBd+JN032AoSGxOL
oxQ55qlJp8bVTBbBX220ZwrnGpl2Q59F7Mwc9KQSUG/6kJ/maqi7l5E//eUgj+CP
+WO82cW5XQmMp5aSZ6hg1dW2dBLDddEZe7/zl43eWp+S8DRByzQofDt+yAKkp7MJ
K5Vdhh4RnMGdAkkg1s7e2osxG090hIfqwV4pE5m7QT4ikNJmxxorvZNETfO+FxCJ
o72i7yMymZWHmKXObvluPvByzqVpuFPldywKvZgHIte730TZ2JZtnArX638/APjD
KH3hJRJNnx8KH2/WWNTek2IY8vuyMMrFukKMzDiglqfL2QGzyWpObMTazhvP/Z2M
MjtWVtMoZeGceUTfSq64HNwzFkZoz3rOPYu1ZYw493PEjqaY5fxIMtBd/kv2m5WS
FUUwBA2eJWKkx0u/cZ91ZAfxSwuGbaplDvr9NzIJXRdtiZRAI4fptFOVzSNvCQ9C
zK7QQRrbbLdFjsMP3VxSfsIJcrcMATSDLm6BAgMBAAGjQjBAMA4GA1UdDwEB/wQE
AwIBBjAPBgNVHRMBAf8EBTADAQH/MB0GA1UdDgQWBBSmHOFtVCRMqI9Icr9uqYzV
5Owx1DANBgkqhkiG9w0BAQsFAAOCAgEAlfp1Y79qJhM2l0QgpaXieeEB7wgURNlA
QRPQOjw24NlmnM4Kquzx9LSVoP1EPlTx+3ku0dGcTYhZiBsLTeOTjMT4j36j5jKT
WfWuuGwQboawtSftbmW7Bsy5x8ZEU7g70toZR2HVlXbaR46ChinHlNu79IXBtWZs
bdG+/btjGrVg6cQAs3U/GAFsoL4G1MkqV6shDvHQNjlzEBbkS0Idcvt+J3sqn/Om
JU8tAqzNx7e2RLUI2ZH4DT1QjVadgik5UVM9AAeTS1gTsIp3nCCid2aF0MpeIZp1
u25E8yQRxBYeVFoj1ZYtxwYfcz3X6A68gIb6ZBeRzPq+wt8bEpS8h47O6hJ5pyNB
bdqBDe6SVdwz7ZUtjGBEJM5oc68j+QNShnke23/duIeK0jopwpyCeEtFckyNjAoL
YsrGG3+MQQGVDqgYZ/W8Ow0AQz/Zt0RfcqQmfCdbxWbTusR8lpZf8reaPU2X5adp
YDpY8W+rVNULcsk/Sj+BZ8YpcrMk90BbXf2K7bbzQw2ODtzHphL6SQkt3Ja6oKLa
WRvQTsaK7K8w+WSilfCtt4u4AAxByxlwmt/KHeMfHZskty/rHLyk7kwgtRzXdvpA
7quYkGyLWtTtZoB5J1b7OYUraMDuqG1s39jMchHjda1GqOwsBWxDqC4HqtdY7TK2
ofZLvqTHvT4=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIFbzCCA1egAwIBAgIUQfJJVcjenVsqV04ke2B6+nMusbowDQYJKoZIhvcNAQEM
BQAwPzEPMA0GA1UEAwwGUm9vdENBMQ0wCwYDVQQLDARMZW5zMRAwDgYDVQQKDAdU
cnVlcGljMQswCQYDVQQGEwJVUzAeFw0yMTEyMDkyMDA0MTdaFw0zNjEyMDUyMDA0
MTZaMD8xDzANBgNVBAMMBlJvb3RDQTENMAsGA1UECwwETGVuczEQMA4GA1UECgwH
VHJ1ZXBpYzELMAkGA1UEBhMCVVMwggIiMA0GCSqGSIb3DQEBAQUAA4ICDwAwggIK
AoICAQDpdmLCqEXVSqb2GVireQbWQHTh9fo3Sja9r9grNAgH4iqEKS7Wlv+zDFmB
lWEfED/e1teBFy8sXqQTZM/nqfEuOAYbepJl535Olp/vOUryAMx78M2svGGug8xG
IOTXGJPekK9sqUwgNe6lgAP7v648d2ygw58MHZ/y20B3XMgiWMwVeM24PHYfQ/bp
zYz3AkG9lrmHbFu8Aily4jFe2b6VI1JbQYgo6DM2uPl7l3VRK2a857+WZioco425
a1xWnv/sJYxPjLEBNq3BDkAJ8vz4DbKnGbRg4mwFQLpLY7bGJVfU14xEbDfuoz/K
ZBO8D2ktLGQGtFdldtsbVGdGvyvuOz/gcwJ/Vq5om7+8OkByiCw50bjU1caReS4q
842VZt1Hw6P3MUsfjHycG+xwYwu0jC4DXl1xCdnuRtYlYZhP8TkUUCN5XeLdnyAm
HIlItkhxin9+2UcNUJUFckyuR2Y5rqAMWZslzb22vPV1QAuBB+wHFPjJrMPMWuw6
3wOBqLAPhplMUp//Ixbo6RXuhs5duNn7Jq6FLx5Eu6sZOiF/MuFnGpvKWr0LrGkP
bnc7wPcFMafh/7Ha9IhAGsD4sELsgTNWcklyPYxLESy+DHptF9nLc+6SAzDPy4ci
qB0tSRS/jehD6hQ5XdTcyYUjjFUlG04uRqImKJhEQVN8/A/GGQIDAQABo2MwYTAP
BgNVHRMBAf8EBTADAQH/MB8GA1UdIwQYMBaAFFi68anyDedFBgqwKadalzDqJz0L
MB0GA1UdDgQWBBRYuvGp8g3nRQYKsCmnWpcw6ic9CzAOBgNVHQ8BAf8EBAMCAYYw
DQYJKoZIhvcNAQEMBQADggIBAIM3f+uTGlEhxinXEASr0MfbUZOK1i58KyDM35Ot
NOHrXv4+z468US40tSYYizto2tpALygkAh0ddywgayOGwLaKR00IkIVwbEH4UVho
pR1QK7PXmmqrF8MTe60TNUiRgC6NUzzKyCIZzIy5e4Q3Cx8uMnNYniaU0TPZeWF9
pWRiIPc7QOZPl3pAUMtHMFv1z5Ww+vJ6iUHKSQFCSs6vy+/fdiWLfdgok6mvXbw1
EE6J6DIypwZU275v5L4UM9b40uNqlSdk6ckraNcj2whsx7D8fpXwKjvkCbihWt8l
gd05fL/7tJBnO/YorriTtBqtUviLnnTc0iEjC5S6yo/HIEWJUL+VK8hH4Tvq4e7Q
W5KC2/hFQ40CyOIuq0QMfjml+Uwp/4zW6LGK+OA09VhQ1dilztXvOE+tZorPTwy5
CPRDi5Mjou6ZQy8LbhSdzrjVJGmEbv/7bsDDxHB/zN4Xb8LrtS89hoGDowu/y/vh
p4/IGuK7iAYb7mLrho0Xl9FUnavgYSm/tMh9UvcZ4Hs5ZeOhdbr5cbxVKDrCGwTs
77U+mI4JBR4WdoORw/CMyjLF7mkO3QZmr0YhTLMdRzn6/yPkotg9OAbLEM1cVZSt
wiz6O4c5amE4Nx+V6hBaLctoD23No45vnrnBDCF3BcVmFcQPBGF450dzKAnuY25a
wpEZ
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----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-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICHDCCAaGgAwIBAgITEkGhSPCEEtphvbOmfJRJGv/f3TAKBggqhkjOPQQDAzA9
MR0wGwYDVQQKExRDb250ZW50U2lnbiBieSBOb2RsZTEcMBoGA1UEAxMTQ29udGVu
dFNpZ24gUm9vdCBDQTAeFw0yMzExMjAyMzIzMzJaFw0zMzExMTcyMzIzMzFaMD0x
HTAbBgNVBAoTFENvbnRlbnRTaWduIGJ5IE5vZGxlMRwwGgYDVQQDExNDb250ZW50
U2lnbiBSb290IENBMHYwEAYHKoZIzj0CAQYFK4EEACIDYgAEKKjUvBHg3eBRpS38
LIuBZ4kfP/pfQw2CzsgT95JqZBrPnlkYvTcEg7tIEriPgVHLC5pXHMSbbQFIYEJ8
YLXHY335sBmhnomZFDM1yqN0P3PK/cfsMKIZ5aIkAhD93fqGo2MwYTAOBgNVHQ8B
Af8EBAMCAQYwDwYDVR0TAQH/BAUwAwEB/zAdBgNVHQ4EFgQUDK+bQAU+9IPexRNP
ElBJtbIbSRIwHwYDVR0jBBgwFoAUDK+bQAU+9IPexRNPElBJtbIbSRIwCgYIKoZI
zj0EAwMDaQAwZgIxAI6PtQ946M7E9Ex4fFb9djgYIbVqJ8Em4ywOFddNAR6DvD8D
u0ZmTtCWBQqWvFxG4gIxAM7eUjCRqT6YLjbsD7eB6k3VPdm46erzQd/Cad820I2E
ThMCsJUSjNKONkZH/JuQJA==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICjzCCAfCgAwIBAgIEXHYjDTAKBggqhkjOPQQDBDBZMQswCQYDVQQGEwJLUjET
MBEGA1UEBxMKU3V3b24gY2l0eTEXMBUGA1UECxMOU2Ftc3VuZyBNb2JpbGUxHDAa
BgNVBAMTE1NhbXN1bmcgY29ycG9yYXRpb24wHhcNMTkwMjI3MDU0MTMzWhcNMzkw
MjIyMDU0MTMzWjBZMQswCQYDVQQGEwJLUjETMBEGA1UEBxMKU3V3b24gY2l0eTEX
MBUGA1UECxMOU2Ftc3VuZyBNb2JpbGUxHDAaBgNVBAMTE1NhbXN1bmcgY29ycG9y
YXRpb24wgZswEAYHKoZIzj0CAQYFK4EEACMDgYYABAGFsa4uumqXkjZYmasTmQRV
k6j52ADjqYqtUl/+yDN/Oza7sz1zVj1mQISKJiSFMUT289tqyZR9fJvCBnYQzfQD
UAE93XbifclsQN+wH/CcwfUByCwnIkU9sRNmLLjYWHCL7YEIDltwd7tKt2REhhKx
0FFooGhmxqnEHSAA6zSNI9Ffk6NjMGEwDwYDVR0TAQH/BAUwAwEB/zAOBgNVHQ8B
Af8EBAMCAQYwHQYDVR0OBBYEFGbsTn+ECfTAKlYSkIP+hkA01S7/MB8GA1UdIwQY
MBaAFGbsTn+ECfTAKlYSkIP+hkA01S7/MAoGCCqGSM49BAMEA4GMADCBiAJCAeGM
gCL5SfTUycZWd+37+cQIFSn5E1AzLIDw1ps1heoWoTj0dM9SPmWBo/TlWZrbtD4G
yH2VI7vz3wkpB9W7oT9RAkIAluAfQFNEqCoYndVEyGhu5RjG412BQdNbh8Y5NzZy
mu4/Zg7pC0ctus6hdJ8J5DjekOEh6tTy8poqNYC+wvHgAJg=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICEzCCAbmgAwIBAgIUVIrV56rbRKoLP81rfblb/2mrQPkwCgYIKoZIzj0EAwIw
WjELMAkGA1UEBhMCR0IxFzAVBgNVBAoMDk1ldGFwaHlzaWMgUFJPMRcwFQYDVQQL
DA5NZXRhcGh5c2ljIFBSTzEZMBcGA1UEAwwQTWV0YXBoeXNpY1Jvb3RDQTAeFw0y
NDA5MDMxMDM4NTBaFw0zNDA5MDExMDM4NTBaMFoxCzAJBgNVBAYTAkdCMRcwFQYD
VQQKDA5NZXRhcGh5c2ljIFBSTzEXMBUGA1UECwwOTWV0YXBoeXNpYyBQUk8xGTAX
BgNVBAMMEE1ldGFwaHlzaWNSb290Q0EwWTATBgcqhkjOPQIBBggqhkjOPQMBBwNC
AAQeOqzGfyAbOjAM4Mf7xeWppTYdolsa7w4BKbXVrBtY5lS4lWrDR3m5JzB31BlL
hR+3pq0AmtdVMz9heQgHD5rRo10wWzAdBgNVHQ4EFgQUC+M9/xDkKCtSA7GVwbFG
YFEH3aowHwYDVR0jBBgwFoAUC+M9/xDkKCtSA7GVwbFGYFEH3aowDAYDVR0TBAUw
AwEB/zALBgNVHQ8EBAMCAQYwCgYIKoZIzj0EAwIDSAAwRQIgPb9+Evw000uDjMQg
3TeRvzhl8+B+03OG5WoyyjZvb90CIQCTDCIltIkUr0/EYTJf6VLKM7mBAZlWX4s2
Bz3E79YeEQ==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICBzCCAaygAwIBAgIUapyBGpjTU7rmwk5Y6gO5m3d2hBYwCgYIKoZIzj0EAwIw
YDELMAkGA1UEBhMCSlAxDjAMBgNVBAgTBVRva3lvMQ8wDQYDVQQHEwZPdGEta3Ux
EzARBgNVBAoTCkNhbm9uIEluYy4xGzAZBgNVBAMTEkNhbm9uIEMyUEEgUm9vdCBD
QTAgFw0yNDEwMTcwNzI1MDZaGA8yMDY0MTAxNjA3MjIxM1owYDELMAkGA1UEBhMC
SlAxDjAMBgNVBAgTBVRva3lvMQ8wDQYDVQQHEwZPdGEta3UxEzARBgNVBAoTCkNh
bm9uIEluYy4xGzAZBgNVBAMTEkNhbm9uIEMyUEEgUm9vdCBDQTBZMBMGByqGSM49
AgEGCCqGSM49AwEHA0IABFnVKwHeC+Cx3gDiLhatGQObilqx5huCx3iQ4dF98gsr
oT1fBIL3yUyWtXK4yt3yfYt/t1sUozjTWQboJiZKLvejQjBAMA8GA1UdEwEB/wQF
MAMBAf8wHQYDVR0OBBYEFNuSgKOKZTMu1E0kQKoSAICI5R7TMA4GA1UdDwEB/wQE
AwIBhjAKBggqhkjOPQQDAgNJADBGAiEA2GLR9KHDjO+0Wf9PKsTckmKeSzvz6cGh
54p6z6Z7lAECIQDx7lT/5ByzxTbYB36Pd6x+9eqkvM69QddPh+pRpvYk/A==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIB4jCCAYegAwIBAgIRAI3b5qRoBIlWVln9WIRMnmMwCgYIKoZIzj0EAwIwTzEL
MAkGA1UEBhMCSlAxHTAbBgNVBAoMFEZVSklGSUxNIENvcnBvcmF0aW9uMSEwHwYD
VQQDDBhGVUpJRklMTSBDMlBBIFJvb3QgQ0EgRzEwIBcNMjQxMjA5MjI1ODQ3WhgP
MjA3NDEyMDkyMzU4MjBaME8xCzAJBgNVBAYTAkpQMR0wGwYDVQQKDBRGVUpJRklM
TSBDb3Jwb3JhdGlvbjEhMB8GA1UEAwwYRlVKSUZJTE0gQzJQQSBSb290IENBIEcx
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE6Xo4h6qtgkxn+LAWDsDU5GSCHYjj
Zm4tHIEmmdyJrZCDYEyXfPhvSS09XKyXIDIEwQcdZU9gAsZdjNjP8cVva6NCMEAw
DwYDVR0TAQH/BAUwAwEB/zAdBgNVHQ4EFgQUjBEQ4pKsJIoIJzI2jJqKQ+78Mfww
DgYDVR0PAQH/BAQDAgGGMAoGCCqGSM49BAMCA0kAMEYCIQDTnTnmnXrgRZpdDY9i
YlXwoF5tJpTaU30UFLUO9PdtpAIhAMoOjajOcuooANmO4ZSFobiaw6wGndE0BxyZ
ic3gTwMF
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----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-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICmzCCAiCgAwIBAgIUAJqo+ansD9x4r/iaYV5AaQX5jQAwCgYIKoZIzj0EAwMw
fDELMAkGA1UEBhMCVVMxDjAMBgNVBAgTBVRleGFzMQ8wDQYDVQQHEwZBdXN0aW4x
GjAYBgNVBAoTEUFUT00gVGVjaG5vbG9naWVzMRYwFAYDVQQLEw1BVE9NIFNlY3Vy
aXR5MRgwFgYDVQQDEw9BVE9NIHJvb3QgQ0EgdjEwHhcNMjQwOTA5MTkwODM4WhcN
MzQwOTA3MTkwODM3WjB8MQswCQYDVQQGEwJVUzEOMAwGA1UECBMFVGV4YXMxDzAN
BgNVBAcTBkF1c3RpbjEaMBgGA1UEChMRQVRPTSBUZWNobm9sb2dpZXMxFjAUBgNV
BAsTDUFUT00gU2VjdXJpdHkxGDAWBgNVBAMTD0FUT00gcm9vdCBDQSB2MTB2MBAG
ByqGSM49AgEGBSuBBAAiA2IABH0eAjxr8/wDSO6EfnE574peDRuGVRr0dTvnwoMl
MilfqJDPe873y3GmzOEj0nTGB/AHPNW1HOKePYoEaK51/66peq6JOxcVIUShQOwI
U1ZdCSIDbRD0kezWXn7P/9El1aNjMGEwDgYDVR0PAQH/BAQDAgEGMA8GA1UdEwEB
/wQFMAMBAf8wHQYDVR0OBBYEFI3x7N/TGB5W9WHtVnpPqyS0jRNXMB8GA1UdIwQY
MBaAFI3x7N/TGB5W9WHtVnpPqyS0jRNXMAoGCCqGSM49BAMDA2kAMGYCMQCZvUp0
2Zo8gDDMyC1gO+TMTNY6nfZ7XXH1SeV0BaVeBGJLhHnTWfpvkN23+/adwiMCMQD1
p6P/cqH4SNa2P/G0nzPn21Z8SblSfGelbA+EkQf9LyNi/v8o08i4oUrB1vhWbIc=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIBmDCCAUqgAwIBAgIUYASaeSSTolrnAdi1g2Fbv7PDxJEwBQYDK2VwMEoxCzAJ
BgNVBAYTAlVTMREwDwYDVQQIDAhOZXcgWW9yazEVMBMGA1UECgwMVHJ1Zm8gKFJv
b3QpMREwDwYDVQQDDAh0cnVmby5haTAeFw0yNDA4MTIyMjE3MDdaFw0yNTA4MTIy
MjE3MDdaMEoxCzAJBgNVBAYTAlVTMREwDwYDVQQIDAhOZXcgWW9yazEVMBMGA1UE
CgwMVHJ1Zm8gKFJvb3QpMREwDwYDVQQDDAh0cnVmby5haTAqMAUGAytlcAMhABFI
CkU3vtCVG4D2VtxqAmQyKZROrBVEnFl8vpmg4CM+o0IwQDAPBgNVHRMBAf8EBTAD
AQH/MA4GA1UdDwEB/wQEAwICBDAdBgNVHQ4EFgQUXa+ujzojES7nhreHkIzbDqeL
OLkwBQYDK2VwA0EALT8wJCiGqgBqVzWf6xvus5PwAhnGx8/U3/+NIl3uFO3fUhUt
2z62xjVd+G2ivv6AZ4VnCjou757WbNqsY3B5Aw==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICrjCCAhCgAwIBAgIJEJ5cSOlh/nYsMAoGCCqGSM49BAMEMHYxOTA3BgNVBAMM
MHZpdm8gQ29udGVudCBQcm92ZW5hbmNlIGFuZCBBdXRoZW50aWNpdHkgUm9vdCBD
QTELMAkGA1UEBhMCQ04xLDAqBgNVBAoMI3Zpdm8gTW9iaWxlIENvbW11bmljYXRp
b24gQ28uLCBMdGQuMCAXDTI1MDQxNjAyNTUzOFoYDzIwNTUwNDE2MDI1NTM4WjB2
MTkwNwYDVQQDDDB2aXZvIENvbnRlbnQgUHJvdmVuYW5jZSBhbmQgQXV0aGVudGlj
aXR5IFJvb3QgQ0ExCzAJBgNVBAYTAkNOMSwwKgYDVQQKDCN2aXZvIE1vYmlsZSBD
b21tdW5pY2F0aW9uIENvLiwgTHRkLjCBmzAQBgcqhkjOPQIBBgUrgQQAIwOBhgAE
AadjySUWxUJN7q9UtkC1169XLrnhqEcOfB3AAE+uQkehChJjR8mBcTAKCGpQHxem
0qxe13rlWj8scCWExoUy4j/eAUCU1rhA9nsaNmqZuQetemoaFsxB+uzFTan7eC2K
GGUTWyEEJsVpZRnzhV1HLKHJ+gEaRCeYkEkGcxRm6Yic6HzAo0IwQDAPBgNVHRMB
Af8EBTADAQH/MA4GA1UdDwEB/wQEAwIBhjAdBgNVHQ4EFgQUZMn3MXRQRGJUI+Jb
ij8quKQc5GEwCgYIKoZIzj0EAwQDgYsAMIGHAkE2v363BCPhkErUyhrBl5KzCrxy
dzCskvifOy2pj0DHPtE1R8iBMJL9L7SqLRiwWFtzKIDjHzrYva/XGZ3vstpcCgJC
ALYmHM4xMXsNqb1hiWIxi1gqm92ddR+PSbVIJygODlJPiDT0xPEro6kTP7GKaxpH
GQdAQ3jeFxZgws/1Fymnxrbq
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICIDCCAcagAwIBAgIUIvXHQ3rquXoskDjpVb+2YuMICyEwCgYIKoZIzj0EAwIw
bTELMAkGA1UEBhMCSlAxDjAMBgNVBAgTBVRva3lvMRUwEwYDVQQHEwxTaGluYWdh
d2Eta3UxGjAYBgNVBAoTEU5JS09OIENPUlBPUkFUSU9OMRswGQYDVQQDExJOaWtv
biBDMlBBIFJvb3QgQ0EwIBcNMjUwMzA2MDAyODQzWhgPMjA2NTAzMDUwMDI0MTFa
MG0xCzAJBgNVBAYTAkpQMQ4wDAYDVQQIEwVUb2t5bzEVMBMGA1UEBxMMU2hpbmFn
YXdhLWt1MRowGAYDVQQKExFOSUtPTiBDT1JQT1JBVElPTjEbMBkGA1UEAxMSTmlr
b24gQzJQQSBSb290IENBMFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE9wzVICOz
P8cseW53DEbdqyO7BG4FWYWilujIo0csh+3uSmSJGYdg0PBa261uIxj4CO5Q2Ks5
AM4j843TRAIRCqNCMEAwDwYDVR0TAQH/BAUwAwEB/zAdBgNVHQ4EFgQUgrs99zxZ
Shz1QQTOPfUnnro4LFcwDgYDVR0PAQH/BAQDAgGGMAoGCCqGSM49BAMCA0gAMEUC
IQCGT6MDGYwqq1/jM6urTcKH+pIJqOLtoc0+cALTYX+hcwIgI+Iue20LU7DAUf64
lhrU52JWzJ2F0/nHEwxKCR/p92Y=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICNTCCAbugAwIBAgIUczN9H4VpMZo+I4l+f4pDY6JMAOcwCgYIKoZIzj0EAwMw
RzELMAkGA1UEBhMCSlAxGTAXBgNVBAoMEFNPTlkgQ29ycG9yYXRpb24xHTAbBgNV
BAMMFFNPTlkgQzJQQSBSb290IENBIEcyMCAXDTI1MDgwNDAzNTMzM1oYDzIwNjUw
NzI1MDM1MzMyWjBHMQswCQYDVQQGEwJKUDEZMBcGA1UECgwQU09OWSBDb3Jwb3Jh
dGlvbjEdMBsGA1UEAwwUU09OWSBDMlBBIFJvb3QgQ0EgRzIwdjAQBgcqhkjOPQIB
BgUrgQQAIgNiAASLHbxrLtoC+469rNSLnkJjSacR0SFSseGs1teFY8gG5cVVy93Z
qoupA5QMR49indgIC3wqXZAP9aJqVCpUyQL2QN9gHndDpH5JRnmJH9zxXjWq21dD
Lfs8rpNEiJT9XkGjZjBkMBIGA1UdEwEB/wQIMAYBAf8CAQIwHwYDVR0jBBgwFoAU
yhnK7M21f7YIRJTDHYremJuCpkwwHQYDVR0OBBYEFMoZyuzNtX+2CESUwx2K3pib
gqZMMA4GA1UdDwEB/wQEAwIBBjAKBggqhkjOPQQDAwNoADBlAjAZKmS4lqMFBW+Q
sJqQTp66xIHP9WwEU34ig6ckz9x/Je3TNbrN90FLaHTxDCQXJuECMQDJ0IO8tfPK
ZerQ8KDvnksbA35Pb2DGcPqtgxqw29DNbJhxu5o4Mna89A991qyPF6g=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICjDCCAhGgAwIBAgIUKS9xW5O7D2rrjSeBl+MynP8pF0owCgYIKoZIzj0EAwMw
czELMAkGA1UEBhMCSlAxIzAhBgNVBAoTGkN5YmVydHJ1c3QgSmFwYW4gQ28uLCBM
dGQuMT8wPQYDVQQDEzZDeWJlcnRydXN0IGlUcnVzdCBDMlBBIFJvb3QgQ2VydGlm
aWNhdGlvbiBBdXRob3JpdHkgRzEwHhcNMjUwNzMwMDUxMzQ1WhcNNDAwNzI0MDUx
MzQ1WjBzMQswCQYDVQQGEwJKUDEjMCEGA1UEChMaQ3liZXJ0cnVzdCBKYXBhbiBD
by4sIEx0ZC4xPzA9BgNVBAMTNkN5YmVydHJ1c3QgaVRydXN0IEMyUEEgUm9vdCBD
ZXJ0aWZpY2F0aW9uIEF1dGhvcml0eSBHMTB2MBAGByqGSM49AgEGBSuBBAAiA2IA
BDB8+JiYghpSphkIz5RriMmGcPKuPwY1OizPf+hGz2IahGdyIZWLZc/lFzGpBKUF
ba8rbL9RbYbQIC/3/X1K91v3WrX4aC0X3Uixjr4GRfA+tYBIuPKSITvHiAe++wR6
2KNmMGQwEgYDVR0TAQH/BAgwBgEB/wIBAjAOBgNVHQ8BAf8EBAMCAQYwHwYDVR0j
BBgwFoAU/+TJjy4pArwGGk2IJUY8z/Vq7yQwHQYDVR0OBBYEFP/kyY8uKQK8BhpN
iCVGPM/1au8kMAoGCCqGSM49BAMDA2kAMGYCMQCLfT3mtiiNRkHTpnDgPGjjjuZj
aeyWkVI2COOpC1FnKptXLbrjjIQnq40K4pfZtyECMQDW/GoK9UT+8hsccKPl35IB
0EkJAzKgs8AzqaV0lV2PjfJEo2rXqIsSk7SLcPfNTu0=
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIICnTCCAf6gAwIBAgIRAPvDTK/w7D6vl0WjbhX93q0wCgYIKoZIzj0EAwQwaDEL
MAkGA1UEBhMCSUwxJTAjBgNVBAoMHEJyaWEgQXJ0aWZpY2lhbCBJbnRlbGxpZ2Vu
Y2UxMjAwBgNVBAMMKUJyaWEgQXJ0aWZpY2lhbCBJbnRlbGxpZ2VuY2UgQzJQQSBS
b290IENBMB4XDTI1MDgxMzA5NDQwMFoXDTQ1MDgxMzEwNDQwMFowaDELMAkGA1UE
BhMCSUwxJTAjBgNVBAoMHEJyaWEgQXJ0aWZpY2lhbCBJbnRlbGxpZ2VuY2UxMjAw
BgNVBAMMKUJyaWEgQXJ0aWZpY2lhbCBJbnRlbGxpZ2VuY2UgQzJQQSBSb290IENB
MIGbMBAGByqGSM49AgEGBSuBBAAjA4GGAAQBm4KD4uRHzKuT9K5+60mkCmRZtvOV
oPFxqOgnzeIymEL1gcwMSLilogpDGvlCdp3AxlSu85dRH/J7HvtuYwX9pLIBbo6O
1UqbwSqI4NnC78YQN+OFEY1oQFGYga3GnqHmqGc3zVpFCkEOok0Om0vxIE1mBp7f
hFwBzkWQZjgp15+K2uWjRjBEMBIGA1UdEwEB/wQIMAYBAf8CAQEwHQYDVR0OBBYE
FDeC4ZhoAbBHTfS6DUU4N/8h/rifMA8GA1UdDwEB/wQFAwMHBgAwCgYIKoZIzj0E
AwQDgYwAMIGIAkIBScJ6SavNV7yjqsWuyaLCAQL9jrrD7Yv9aqO5EPf+MmcfT8ZM
4n5rqQluxv4ieJ6Jeo0YsbkWYdRiUUlnFuEdbPkCQgFR42wDXEjulMA7mwMjKyBs
Y8Iz2SQmZKEG5OUOEDlDUoxOworLQ9dywuycjdMDWjfMZHaLMeGGofeAqAOLmreB
Qg==
-----END CERTIFICATE-----
-----BEGIN CERTIFICATE-----
MIIFjzCCA3egAwIBAgIQEnO0shHK6IxLTfxxgTIJkDANBgkqhkiG9w0BAQwFADBX
MQswCQYDVQQGEwJVUzEeMBwGA1UEChMVTWljcm9zb2Z0IENvcnBvcmF0aW9uMSgw
JgYDVQQDEx9NaWNyb3NvZnQgQzJQQSBBTDIgUm9vdCBDQSAyMDI1MB4XDTI1MTIx
NjIwNTEzM1oXDTQ1MTIxNjIwNTg1MFowVzELMAkGA1UEBhMCVVMxHjAcBgNVBAoT
FU1pY3Jvc29mdCBDb3Jwb3JhdGlvbjEoMCYGA1UEAxMfTWljcm9zb2Z0IEMyUEEg
QUwyIFJvb3QgQ0EgMjAyNTCCAiIwDQYJKoZIhvcNAQEBBQADggIPADCCAgoCggIB
AMzpMnj7KOLhCDiNkbhHRY28nt+dapImPvfBbI6CVpGh/sCSydJX6ZDI8hCQ184t
88fXwvTOMwrNO5vFRv8x2DuoAMlFm0CuT1rwb4cQcDNkFc/MltC0LX8yy3WjauyL
gS4DsQNy4SYrFW5vyQd7mnqKn3hUoV4tRPSrHtM2/aGIDtw3kFUgeMdHwIP68gn0
r2nmmNazoUs3hSF+lulEpKX6m0aIbWuNZQHIM+OuvELZD/Mi4VKI0awfa0c57une
KLNV3s1R1wev3D5ZeUYVChkpgiYOpjIaGzpMzLXqiE/L5q0sVJrzO+Ada2yX2MAy
AmCGPa+u2gsSqwFnFSNPpi9u6KYjmJuOCyPaXTE/MNlPK/pvpbkoSQG+GI2j1ST5
i9xqw39bAvZzWrlZWv+tNrvDaKMc/1uUOJuIpmqtMfzdJfkaf6djItEJi+vGhwMp
fD2WOjxoaPMjP0Fp+GuSDwHgz9q2E8qtTbnKGd9ZbewhjPIu9voiQSGP9I2nMiyS
uVbJ2IuYW6X2KjMU6+fxkjK/1c2rfY7W3od67IVGSzKVkP/aiZAX1iqikD7W+fMP
cgPF7qCPO2mL7TbfVv3C/Yz5BItgw5KM3iVFV6wjOoYHWRdwXI4bMFwbgJxagvso
RGA8kVP5ZPrx04FW+qKBMidVfQkJNZSz6mH+j4bw9H4dAgMBAAGjVzBVMA4GA1Ud
DwEB/wQEAwIBBjASBgNVHRMBAf8ECDAGAQH/AgECMB0GA1UdDgQWBBRlk7SgJPWG
qFZLi0w+GI13Cfjq4TAQBgkrBgEEAYI3FQEEAwIBADANBgkqhkiG9w0BAQwFAAOC
AgEAfqfoE/Rpe02glLPpuy0gvBIxi7YKlur5hOB8CCsYAA8EatP54GqBEfAXOaSJ
1x2H7dYkbn6lK9hpY8+cEtXPqiEafLCMoe2FvD9YI16fS0EVn5+qvnNSvKIULQpO
xeObJCnemwudMPcKBIjZhRgysZucQJcvGRD7ECpatzugUKx8JmC8xQv4YMrCBXdY
R19S5REYzfh3S/koUUd2AkEkkNtEPzzC+LjWL9zY3RderiD536TYl7Ej3HQ8QlVW
3CwMFwqC6I8Nfmb+hmSsvJxGwd45P9IOrnQGNUlBvnXapYFl4h9H46DgJ9ViAruM
MTSSKGUmLygBu8tj7aIHtSjBzt5MDWWHBy9w+tEjNY35eEpMNaLpG7R00/zNNXT5
vfqatpFxtLIKkesY4vCv8FJpZAoBMKGodAtTsrt+7lspQlnC8eNZNu/s/QDK/Li7
hwXFmpP4Jlp+Af7tL+ZF9+4UMfEEts6H6xbREYS+5XJRLlWDZkrxThU4D4Iz7V6A
edg2W+70uokQWuqqwACzQvQMlrC1ccUd7/2Ld5DpVcy8vXd+GqQqgWC3zlVUZf+1
f2qnVH+ewkpo6VmeVXOdiCgfQJIS8rkEMJfjmQkEYZj0qOD4Oof+BxnrUGaSGrBr
dbTpTnNA8vv2MUw5th3vRbfKMlAOXtgaJligciyEyqo/ObE=
-----END CERTIFICATE-----
`

export const DEFAULT_TRUST_CONFIG = `//id-kp-emailProtection 
1.3.6.1.5.5.7.3.4
//id-kp-documentSigning 
1.3.6.1.5.5.7.3.36
//id-kp-timeStamping 
1.3.6.1.5.5.7.3.8
//id-kp-OCSPSigning 
1.3.6.1.5.5.7.3.9
// MS C2PA Signing
1.3.6.1.4.1.311.76.59.1.9
// c2pa-claim_signing OID
1.3.6.1.4.1.62558.2.1`
